// External libraries
import { validateName } from '@checkout/react-components';

// Internal modules
import { appStore } from '@/store';
import { logger } from '@/lib/logger';
import { features } from '@/lib/features';
import { filterObject } from '@/lib/utils/miscUtils';
import { validateAddressFields } from '../../Address/utils';
import {
  getBillingNameFromCart,
  getPhoneValidator,
  validateEmail,
} from './miscUtils';

const getPrefillPayloadForField = (value, validationFn) => {
  const { isValid, errors } = validationFn(value);

  return {
    errorCode: errors[0] || '',
    isValid,
    value,
  };
};

/**
 * @function prefillNonAddressFields
 * @param {object} data
 */
const prefillNonAddressFields = ({ cart, phoneMeta }) => {
  const { email, phoneNumber } = cart;
  const { form: formState } = appStore.getState();
  const validatePhoneNumber = getPhoneValidator(phoneMeta);
  const { firstName, lastName } = getBillingNameFromCart(cart);

  const actions = [
    { field: 'email', validator: validateEmail, value: email?.stringValue },
    { field: 'firstName', validator: validateName, value: firstName },
    { field: 'lastName', validator: validateName, value: lastName },
    { field: 'phone', validator: validatePhoneNumber, value: phoneNumber },
  ];

  const prefillActions = actions.filter(({ field, value }) => {
    const currentStoreValue = formState[field].value;
    // If the value is not passed or the user has already entered
    // a value into the field then we will not prefill it.
    return Boolean(value) && !currentStoreValue;
  });

  const { form: formActions } = appStore.getActions();
  prefillActions.forEach(({ field, validator, value }) => {
    formActions.updateNonAddressField({
      field,
      payload: getPrefillPayloadForField(value, validator),
    });
  });

  if (prefillActions.length) {
    logger.info('weasley_prefill_form_actions', {
      actions: prefillActions.map(({ field }) => field),
    });
  }
};

/**
 * @param prefillAddressFields
 * @param {object} { addressMeta, shippingAddress }
 * @description Prefill address fields with data sent from merchant
 */
const prefillAddressFields = ({ addressMeta, cart }) => {
  const { form: formState } = appStore.getState();
  const { form: formActions } = appStore.getActions();

  // 1. Filter out `null` and empty strings as well as fields that the user
  // has already started to fill out.
  // 2. filter _typename key from both billing and shipping address objects,
  // so that length will be zero and do early return when address is
  // null and no need to update the store.
  // {"__typename":"CartAddress"} - for billing
  // {"__typename":"CartShippingAddress"} - for shipping
  const billingAddress = filterObject(
    cart.billingAddress,
    ([key, value]) =>
      key !== '__typename' && !formState.billingAddress?.[key]?.value && value,
  );
  const shippingAddress = filterObject(
    cart.shippingAddress,
    ([key, value]) =>
      key !== '__typename' && !formState.shippingAddress?.[key]?.value && value,
  );
  const isAddressless = features.isActive('isAddressless');
  const shouldPrefillBilling = Boolean(Object.keys(billingAddress).length);
  const shouldPrefillShipping = Boolean(Object.keys(shippingAddress).length);

  // Early return if don't need to prefill any fields
  if ((!shouldPrefillBilling || isAddressless) && !shouldPrefillShipping) {
    logger.info('no_prefill_required', {
      isAddressless,
      shouldPrefillBilling,
      shouldPrefillShipping,
    });
    return;
  }

  /**
   * 1. If we are in an address-less flow we won't be showing the address
   *    fields. In this case we will not be sending the billing address on
   *    form submission so we don't need to update it in the store.
   * 2. If the billing address was not sent by the merchant and shipping
   *    address is a store address (ship to store) then we do not prefill the
   *    billing address.
   */
  const { isStoreAddress } = cart?.shippingAddress || {};
  if (!isAddressless && (shouldPrefillBilling || !isStoreAddress)) {
    // Merchants occasionally pass the postal code with leading/trailing
    // whitespace. We need to trim this whitespace because it won’t pass client
    // side validation which in turn will block form submission.
    if (billingAddress.hasOwnProperty('postalCode')) {
      billingAddress.postalCode = billingAddress.postalCode?.trim();
    }
    formActions.updateAllAddressFieldsUsingMerchantPrefilledData({
      addressType: 'billingAddress',
      isBatchUpdate: true,
      payload: getPrefillAddressPayload(
        shouldPrefillBilling ? billingAddress : shippingAddress,
        addressMeta,
      ),
    });

    logger.info('prefill_billing_address', {
      isStoreAddress: cart.isStoreAddress,
      shouldPrefillBilling,
      shouldPrefillShipping,
    });
  }

  if (shouldPrefillShipping) {
    // If the shipping address is a store address then we do not want the
    // shipt to billing checkbox checked.
    if (isStoreAddress) {
      formActions.updateShipToBilling(false);
    }

    // Merchants occasionally pass the postal code with leading/trailing
    // whitespace. We need to trim this whitespace because it won’t pass client
    // side validation which in turn will block form submission.
    if (shippingAddress.hasOwnProperty('postalCode')) {
      shippingAddress.postalCode = shippingAddress.postalCode?.trim();
    }

    // The shipping address also has a first and last name
    // that need to be validated
    const shippingPayload = ['firstName', 'lastName'].reduce(
      (accum, field) => {
        const value = shippingAddress[field];
        if (value) {
          accum[field] = getPrefillPayloadForField(value, validateName);
        }
        return accum;
      },
      getPrefillAddressPayload(shippingAddress, addressMeta),
    );

    formActions.updateAllAddressFieldsUsingMerchantPrefilledData({
      addressType: 'shippingAddress',
      isBatchUpdate: true,
      payload: shippingPayload,
    });

    logger.info('prefill_shipping_address');
  }
};

/**
 * @param prefillFormFields
 * @param {object} { cart, addressMeta }
 * @description Prefills user info with data sent from merchant
 */
const prefillFormFields = ({ addressMeta, cart, phoneMeta }) => {
  prefillAddressFields({ addressMeta, cart });
  prefillNonAddressFields({ cart, phoneMeta });
};

const getPrefillAddressPayload = (address, addressMeta) => {
  const validatedAddress = validateAddressFields({
    address,
    addressMeta,
    shouldUpdateValue: true,
  });

  return Object.entries(validatedAddress).reduce(
    (accum, [key, data]) => ({
      ...accum,
      ...(data.value && { [key]: data }),
    }),
    {},
  );
};

export { prefillAddressFields, prefillFormFields, prefillNonAddressFields };
